/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useState } from 'react';
import useApi from '@/lib/api/useApi';
import PortfolioItemContext from '@/components/ChallengePortfolio/NormalView/portfolio-item/PortfolioItemContext';
import { type CoachComment } from '@/lib/types';
import AddCoachCommentButton from './AddCoachCommentButton';
import TextEditor from '../../TypeSpecificFieldRenderers/utils/TextEditor';
import Space from '@/components/Space';
import CoachCommentsContext from '../CoachCommentsContext';
import isTouchDevice from '@/lib/utils/isTouchDevice';
import MobileCoachCommentButton from './MobileCoachCommentButton';
import ReplyIndicator from '../ReplyIndicator';
import CoachCommentFormTitle from './CoachCommentFormTitle';

type AddCoachCommentProps = {
  onShowAddForm: () => void;
  onCloseAddForm: () => void;
  isFirstCoachComment: boolean;
};

const AddCoachComment = ({
  onShowAddForm,
  onCloseAddForm,
  isFirstCoachComment,
}: AddCoachCommentProps) => {
  const api = useApi();

  const { setPortfolioItem, setPortfolioItemSummary } =
    useContext(PortfolioItemContext);
  const { addCoachCommentApiUrl, isInLogEntry } =
    useContext(CoachCommentsContext);

  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const openAddForm = (): void => {
    setAdding(true);

    onShowAddForm();
  };

  const closeAddForm = (): void => {
    setAdding(false);

    onCloseAddForm();
  };

  const saveNewCoachComment = useCallback(
    async (content: string): Promise<void> => {
      closeAddForm();

      setSaving(true);

      const coachComment = await api.post<CoachComment>(addCoachCommentApiUrl, {
        content,
      });

      setPortfolioItem(portfolioItem => {
        portfolioItem.coachComments[coachComment.id] = coachComment;
      });

      setPortfolioItemSummary(portfolioItemSummary => {
        portfolioItemSummary._count.coachComments++;
      });

      setSaving(false);
    },
    [],
  );

  if (!adding && isFirstCoachComment && isTouchDevice()) {
    return (
      <MobileCoachCommentButton
        loading={saving}
        onClick={openAddForm}
        isInLogEntry={isInLogEntry}
      />
    );
  }

  if (!adding) {
    return <AddCoachCommentButton onClick={openAddForm} loading={saving} />;
  }

  return (
    <section>
      {isFirstCoachComment ? (
        <ReplyIndicator isPurple />
      ) : (
        <Space height={isInLogEntry ? 12 : 18} />
      )}
      <CoachCommentFormTitle isPurple={isFirstCoachComment} />
      <TextEditor
        onCancel={closeAddForm}
        onSave={saveNewCoachComment}
        disabled={saving}
        disableSaveWhenEmpty
      />
    </section>
  );
};

export default AddCoachComment;
